/**
 * F-073 — Texas lead qualification factors (E-022 FE, Batch-8).
 *
 * Pure derivation over the EXISTING canonical LP lead-scoring shape
 * (src/lib/lead-scoring.js, mounted at /api/lead-scoring):
 *   { leadId, email, source, propertyCount, segment, location:{city,state},
 *     engagement, score, breakdown, tier }
 * No new BE routes.
 *
 * The LP scoring engine publishes breakdown = { property, segment, location,
 * engagement }. The E-022 TX-factor rows (SB 38, Subchapter D gaps, municipal
 * registration, budget) are declared as keys below so each row lights up the
 * moment buffy's E-022 BE publishes them — until then a row reports
 * `pending` instead of inventing a value.
 */
import { LeadScore, tierOf } from './leadScoring';

export type TexasCity = 'Houston' | 'Dallas' | 'San Antonio' | 'Austin' | 'Other';

export const TEXAS_MAJOR_CITIES: TexasCity[] = ['Houston', 'Dallas', 'San Antonio', 'Austin'];

export interface TexasFactorRow {
    key: string;
    label: string;
    value: string | number | boolean | null;
    met: boolean;
    pending: boolean;
}

export interface TexasLeadFactors {
    propertyCount: number;
    city: TexasCity;
    state: string;
    isTexas: boolean;
    segment: string;
    highQuality: boolean;
    factors: TexasFactorRow[];
}

export interface TexasLeadSummary {
    total: number;
    texas: number;
    highQuality: number;
    byCity: Array<{ city: TexasCity; count: number }>;
}

/**
 * Mirrors LP src/lib/email-cadence.js segmentForPropertyCount so the F-073
 * segment shown on a lead is the same segment F-074's cadence enrols.
 * Returns null for unknown/zero counts — callers must not guess.
 */
export function segmentForPropertyCount(propertyCount: unknown): string | null {
    const n = Number(propertyCount);
    if (!Number.isFinite(n) || n < 1) return null;
    if (n === 1) return 'independent';
    if (n <= 10) return 'small-portfolio';
    if (n <= 50) return 'mid-portfolio';
    if (n <= 200) return 'large-portfolio';
    return 'enterprise';
}

const CITY_ALIASES: Record<string, TexasCity> = {
    houston: 'Houston',
    dallas: 'Dallas',
    'san antonio': 'San Antonio',
    austin: 'Austin',
};

export function texasCityOf(lead: Pick<LeadScore, 'location'>): TexasCity {
    const raw = String((lead.location && lead.location.city) || '').trim().toLowerCase();
    return CITY_ALIASES[raw] || 'Other';
}

export function isTexasLead(lead: Pick<LeadScore, 'location'>): boolean {
    const state = String((lead.location && lead.location.state) || '').trim().toLowerCase();
    return state === 'tx' || state === 'texas';
}

/** High-quality = the engine's HOT band (score >= 70 / tier HOT). */
export function isHighQualityLead(lead: LeadScore): boolean {
    return tierOf(lead) === 'Hot';
}

export function texasMarketSegmentOf(lead: LeadScore): string {
    const explicit = lead.segment && String(lead.segment).trim();
    if (explicit) return String(explicit);
    return segmentForPropertyCount(lead.propertyCount) || 'unsegmented';
}

interface FactorDef {
    key: string;
    label: string;
    keys: string[];
    test: (value: unknown) => boolean;
}

/**
 * E-022 TX-factor rows. `keys` are tried in order against lead.breakdown and
 * then the lead itself — first defined value wins.
 */
const TX_FACTOR_DEFS: FactorDef[] = [
    { key: 'sb38', label: 'SB 38 notice', keys: ['sb38', 'sb38Notice', 'sb_38'], test: (v) => Boolean(v) },
    {
        key: 'subchapterD',
        label: 'Subchapter D gaps',
        keys: ['subchapterD', 'subchapterDGaps', 'subchapter_d'],
        test: (v) => Number(v) === 0,
    },
    {
        key: 'municipal',
        label: 'Municipal registration',
        keys: ['municipal', 'municipalRegistration', 'registrations'],
        test: (v) => Boolean(v),
    },
    { key: 'budget', label: 'Budget', keys: ['budget'], test: (v) => Number(v) > 0 },
];

const firstDefined = (sources: Array<Record<string, unknown> | undefined>, keys: string[]): unknown => {
    for (const source of sources) {
        if (!source) continue;
        for (const key of keys) {
            const value = source[key];
            if (value !== undefined && value !== null && value !== '') return value;
        }
    }
    return undefined;
};

export function texasFactorsFromLead(lead: LeadScore): TexasLeadFactors {
    const breakdown = (lead.breakdown || {}) as Record<string, unknown>;
    const self = lead as unknown as Record<string, unknown>;
    const city = texasCityOf(lead);
    const isTexas = isTexasLead(lead);
    const propertyCount = Number(lead.propertyCount) || 0;
    const sources = [breakdown, self];

    const factors: TexasFactorRow[] = [
        {
            key: 'propertyCount',
            label: 'Property count',
            value: propertyCount,
            met: propertyCount >= 1,
            pending: false,
        },
        { key: 'city', label: 'Texas market (city)', value: city, met: isTexas, pending: false },
    ];

    for (const def of TX_FACTOR_DEFS) {
        const value = firstDefined(sources, def.keys);
        const pending = value === undefined;
        factors.push({
            key: def.key,
            label: def.label,
            value: pending ? null : (value as string | number | boolean),
            met: pending ? false : def.test(value),
            pending,
        });
    }

    return {
        propertyCount,
        city,
        state: String((lead.location && lead.location.state) || ''),
        isTexas,
        segment: texasMarketSegmentOf(lead),
        highQuality: isHighQualityLead(lead),
        factors,
    };
}

export function outreachLeads(leads: LeadScore[]): LeadScore[] {
    return leads.filter((lead) => isTexasLead(lead) && isHighQualityLead(lead));
}

export function summarizeTexasLeads(leads: LeadScore[]): TexasLeadSummary {
    const texasLeads = leads.filter(isTexasLead);
    const byCity = (['Houston', 'Dallas', 'San Antonio', 'Austin'] as TexasCity[]).map((city) => ({
        city,
        count: texasLeads.filter((lead) => texasCityOf(lead) === city).length,
    }));
    return {
        total: leads.length,
        texas: texasLeads.length,
        highQuality: texasLeads.filter(isHighQualityLead).length,
        byCity,
    };
}

export default texasFactorsFromLead;
